import { TEAM_DATA, type TeamKey } from '../../models/TeamConstants';
import type { UserAchievement } from '../../models/UserAchievement';
import type { LeaderboardPeriod } from '../../hooks/useLeaderboardData';

export interface ParsedAchievement {
  achievementType: string;
  periodType: 'week' | 'month' | 'alltime';
  rank: number;
  isTeam: boolean;
  teamKey: TeamKey | null;
  icon: string;     // emoji string to render
  label: string;    // short label (used as fallback / aria)
}

const RANK_MEDALS: Record<number, string> = {
  1: '🥇',
  2: '🥈',
  3: '🥉',
};

const RANK_ORDINAL: Record<number, string> = {
  1: '1st',
  2: '2nd',
  3: '3rd',
};

// Period type sort priority — alltime first, then monthly, then weekly (individual then team)
const PERIOD_SORT_ORDER: Record<ParsedAchievement['periodType'], number> = {
  alltime: 0,
  month:   1,
  week:    2,
};

// Parses an achievement_type string into display-ready data.
// Supported formats:
//   "alltime_top_1"                    → all-time individual top 3
//   "week_top_1" / "month_top_1"       → weekly/monthly individual top 3
//   "week_team_griffins_top_2"         → weekly/monthly team top 3
export function parseAchievementType(achievementType: string): ParsedAchievement | null {
  // All-time individual: alltime_top_1
  const alltimeMatch = achievementType.match(/^alltime_top_(\d)$/);
  if (alltimeMatch) {
    const rank = parseInt(alltimeMatch[1]);
    const medal = RANK_MEDALS[rank] ?? '🏅';
    return {
      achievementType,
      periodType: 'alltime',
      rank,
      isTeam: false,
      teamKey: null,
      icon: `⭐${medal}`,
      label: `All-Time #${rank}`,
    };
  }

  // Weekly/monthly individual: week_top_1 / month_top_2
  const overallMatch = achievementType.match(/^(week|month)_top_(\d)$/);
  if (overallMatch) {
    const periodType = overallMatch[1] as 'week' | 'month';
    const rank = parseInt(overallMatch[2]);
    const medal = RANK_MEDALS[rank] ?? '🏅';
    return {
      achievementType,
      periodType,
      rank,
      isTeam: false,
      teamKey: null,
      icon: medal,
      label: `${periodType === 'week' ? 'Weekly' : 'Monthly'} #${rank}`,
    };
  }

  // Weekly/monthly team: week_team_griffins_top_2 / month_team_snakes_top_1
  const teamMatch = achievementType.match(/^(week|month)_team_([a-z]+)_top_(\d)$/);
  if (teamMatch) {
    const periodType = teamMatch[1] as 'week' | 'month';
    const teamRaw = teamMatch[2];
    const rank = parseInt(teamMatch[3]);
    const medal = RANK_MEDALS[rank] ?? '🏅';

    // Match team name case-insensitively against TEAM_DATA keys
    const teamKey = (Object.keys(TEAM_DATA) as TeamKey[]).find(
      k => k.toLowerCase() === teamRaw.toLowerCase()
    ) ?? null;
    const teamEmoji = teamKey ? TEAM_DATA[teamKey].image : '';

    return {
      achievementType,
      periodType,
      rank,
      isTeam: true,
      teamKey,
      icon: `${teamEmoji}${medal}`,
      label: `${teamKey ?? teamRaw} ${periodType === 'week' ? 'Weekly' : 'Monthly'} #${rank}`,
    };
  }

  return null;
}

// Returns a full human-readable description for a parsed achievement.
// TODO: Fine-tune wording once badge designs are finalised
export function getAchievementDescription(parsed: ParsedAchievement): string {
  const ordinal = RANK_ORDINAL[parsed.rank] ?? `#${parsed.rank}`;

  if (parsed.periodType === 'alltime') {
    return `Ranked ${ordinal} on the all-time leaderboard`;
  }

  const periodLabel = parsed.periodType === 'week' ? 'weekly' : 'monthly';

  if (parsed.isTeam && parsed.teamKey) {
    return `Finished ${ordinal} on the ${periodLabel} ${parsed.teamKey} team leaderboard`;
  }

  return `Finished ${ordinal} on the ${periodLabel} leaderboard`;
}

// Groups achievements by type and counts occurrences, filtered to the active period.
// All-time achievements are always included regardless of the active period filter,
// since they are the most prestigious and should surface everywhere.
export function groupAchievementsForPeriod(
  achievements: UserAchievement[],
  activePeriod: LeaderboardPeriod
): Array<{ parsed: ParsedAchievement; count: number }> {
  const filtered = achievements.filter(a => {
    const parsed = parseAchievementType(a.achievementType);
    if (!parsed) return false;
    // All-time achievements always show, regardless of active period
    if (parsed.periodType === 'alltime') return true;
    // For the alltime leaderboard view, show all achievement types
    if (activePeriod === 'alltime') return true;
    // Otherwise filter to the matching period
    return parsed.periodType === activePeriod;
  });

  // Count by achievement type
  const countMap = new Map<string, number>();
  for (const a of filtered) {
    countMap.set(a.achievementType, (countMap.get(a.achievementType) ?? 0) + 1);
  }

  // Build result, deduplicated by type
  const seen = new Set<string>();
  const result: Array<{ parsed: ParsedAchievement; count: number }> = [];
  for (const a of filtered) {
    if (seen.has(a.achievementType)) continue;
    seen.add(a.achievementType);
    const parsed = parseAchievementType(a.achievementType);
    if (parsed) result.push({ parsed, count: countMap.get(a.achievementType) ?? 1 });
  }

  // Sort: alltime → monthly → weekly individual → weekly team, then rank ascending within group
  return result.sort((a, b) => {
    const periodDiff = PERIOD_SORT_ORDER[a.parsed.periodType] - PERIOD_SORT_ORDER[b.parsed.periodType];
    if (periodDiff !== 0) return periodDiff;
    // Within same period: individual before team
    if (a.parsed.isTeam !== b.parsed.isTeam) return a.parsed.isTeam ? 1 : -1;
    return a.parsed.rank - b.parsed.rank;
  });
}